/**
 * Centralized Portfolio Data Store
 * ================================
 * This file serves as the single source of truth for all portfolio content.
 * Update data here to reflect changes across all components.
 */

export interface HeroData {
  name: string;
  greeting: string;
  headline: string;
  subheading: string;
  tagline: {
    line1Part1: string;
    line1Part2: string;
    line1Part3: string;
    line2Part1: string;
    line2Part2: string;
  };
  description: string;
  ctaText: string;
  ctaLink: string;
  profileImage: string;
  socialLinks?: SocialLink[];
  stats?: { label: string; value: string; href: string }[];
  resumeUrl?: string;
  roles?: string[];
  quote?: string;
  availabilityStatus?: string;
}

export interface AboutData {
  intro: string;
  body: string[];
  currentFocus: string;
  location: string;
  buildStatuses: string[];
}

export interface SectionMetadata {
  sectionNumber: string;
  sectionLabel: string;
  title?: string;
  subtitle?: string;
  description?: string;
}

export interface SkillCategory {
  title: string;
  skills: Skill[];
}

export interface Skill {
  name: string;
  level: number;
}

export interface Project {
  id?: string;
  title: string;
  description: string;
  tags: string[];
  year: string;
  link?: string;
  githubUrl?: string;
  liveUrl?: string;
  image?: string;
  coverImage?: string; // landscape 1200x800 mockup, used for horizontal cards (hire/best pages) - distinct from the portrait `image` used on the /projects listing and detail pages
  images?: string[];
  logo?: string;
  video?: string;
  featured?: boolean;
  isVisibleInAppOS?: boolean;
  isVisibleInProjectsPage?: boolean;
  isVisibleInHomeProjects?: boolean;
  tagline?: string;
  category?: string;
  downloads?: string;
  rating?: number;
  platform?: string[];
  color?: string;
  type?: "app" | "website";
}

export interface Experience {
  role: string;
  company: string;
  period: string;
  description: string;
  skills: string[];
  achievements: string[];
  type?: "professional" | "freelance" | "contract";
}

export interface Education {
  school: string;
  degree: string;
  year: string;
  logo: string;
  grade?: string;
  activities?: string[];
}

export interface PhilosophyPrinciple {
  title: string;
  description: string;
  icon?: string;
}

export interface SocialLink {
  name: string;
  href: string;
  icon: string;
  color?: string;
}

export interface ContactData {
  sectionNumber: string;
  sectionLabel: string;
  headline: string;
  description: string;
  email: string;
  cta: string;
  socials: SocialLink[];
  spotifyWidgetEnabled: boolean;
  location: string;
  onlineStatus: string;
  whatsappNumber: string;
}

export interface MobileApp {
  id: number;
  name: string;
  tagline: string;
  description: string;
  category: string;
  downloads: string;
  rating: number;
  platform: string[];
  year: string;
  color: string;
  tech: string[];
  screens: string[];
}

export interface WorkShowcaseItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  gradient: string;
  iconColor: string;
  glowColor: string;
  images: string[];
  stats: Record<string, string>;
}

export interface HireMeItem {
  text: string;
  color: string;
  delay: number;
}

export interface Hackathon {
  name: string;
  date: string;
  status: string;
  color: string;
}

// ============================================================================
// DATA CONSTANTS (Migrated from Supabase)
// ============================================================================

export const HERO_DATA: HeroData = {
  name: "Sachin Sharma",
  greeting: "hi there, I'm",
  headline: "Software Developer & Mobile Engineer",
  subheading: "Transforming complex ideas into seamless, scalable, and high-performance digital realities.",
  tagline: {
    line1Part1: "Building",
    line1Part2: "Scalable",
    line1Part3: "Mobile Products",
    line2Part1: "that deliver",
    line2Part2: "Real Value",
  },
  description:
    "A 20 year-old developer from India who loves building efficient, scalable, and intuitive applications. With over 1.5 years of hands-on experience, I build production-ready applications that solve real problems. Specializing in cross-platform development, cloud services, and intuitive design.",
  ctaText: "View Selected Work",
  ctaLink: "#projects",
  profileImage: "/sachinsharma.webp",
  socialLinks: [
    {
      name: "github",
      href: "https://github.com/maisachinsharmahu",
      icon: "Github",
      color: "hover:text-foreground",
    },
    {
      name: "linkedin",
      href: "https://www.linkedin.com/in/sachinsharmaofficial",
      icon: "Linkedin",
      color: "hover:text-[#0077B5]",
    },
    {
      name: "email",
      href: "mailto:sachinsharmaaofficial@gmail.com",
      icon: "Mail",
      color: "hover:text-primary",
    },
  ],

  stats: [
    { label: "Years Exp", value: "1.5+", href: "#experience" },
    { label: "Projects", value: "10+", href: "#projects" },
  ],
  resumeUrl: "/resume",
  roles: ["Mobile App Developer", "Software Designer", "Fullstack Engineer"],
  quote: "Crafting digital experiences that bridge functionality and aesthetics.",
  availabilityStatus: "Available for new projects",
};

export const SECTION_METADATA: Record<string, SectionMetadata> = {
  hero: {
    sectionNumber: "01",
    sectionLabel: "Introduction",
  },
  about: {
    sectionNumber: "02",
    sectionLabel: "About Me",
    title: "About Me",
    description: "I am a software developer with a passion for engineering outcomes.",
  },
  projects: {
    sectionNumber: "03",
    sectionLabel: "Selected Work",
    title: "Selected Work",
    description: "A showcase of my recent projects and contributions.",
  },
  skills: {
    sectionNumber: "04",
    sectionLabel: "Technical Skills",
    title: "Technical Skills",
    description: "Technologies and tools I work with.",
  },
  experience: {
    sectionNumber: "05",
    sectionLabel: "Experience",
    title: "Experience",
    description: "My professional journey.",
  },
  contact: {
    sectionNumber: "06",
    sectionLabel: "Contact",
    title: "Get in Touch",
    description: "Let's build something extraordinary.",
  },
  education: {
    sectionNumber: "07",
    sectionLabel: "Education",
    title: "Education",
  },
  philosophy: {
    sectionNumber: "08",
    sectionLabel: "Philosophy",
    title: "Philosophy",
  },
  hireMe: {
    sectionNumber: "09",
    sectionLabel: "Hire Me",
    title: "Available for Hire",
  },
  workShowcase: {
    sectionNumber: "10",
    sectionLabel: "Showcase",
  },
};

export const ABOUT_DATA: AboutData = {
  intro:
    "I'm Sachin Sharma, a software developer and mobile engineer from Delhi. I build things that ship and hold up in production, not just demos.",
  body: [
    "I started with a Diploma in Computer Science at Ambedkar DSEU, then took a lateral entry into B.Tech at MAIT. Along the way I interned at Kushal Aide building my first real production Flutter app, then joined ESPO as a Frontend Developer Intern and got promoted to Software Developer Intern in under two months, leading mobile feature work on their Android and iOS apps.",
    "Since July 2026 I've been working directly with a family-run business conglomerate in Chhattisgarh, ₹150 Cr+ revenue across automobile, retail, and real estate, who found me through organic Google search. I built their multi-tenant dealership platform from scratch, plus their brand website, replacing a manual paper process and cutting lead-to-delivery turnaround from 2 weeks to 4 days.",
    "Outside paid work I build my own products end to end: Loopin, Legease, and Aashwasan, all real, shipped codebases, not tutorials, and I've been a hackathon finalist three times. I care more about whether something ships and holds up in production than how good the demo looks, that's the whole approach.",
  ],
  currentFocus:
    "Freelancing full-stack for a ₹150 Cr+ revenue business conglomerate, while building Loopin, Legease, and Aashwasan on the side and exploring advanced cross-platform system design patterns.",
  location: "Delhi, India",
  buildStatuses: [
    "Building Loopin",
    "Shipping weekly",
    "Refactoring UI",
    "Debugging prod 😄",
    "Building GamerGazzate",
    "Optimizing performance",
    "Freelancing full-stack",
    "Open Source",
  ],
};

export const SKILLS_DATA: SkillCategory[] = [
  {
    title: "Mobile Development",
    skills: [
      { name: "Flutter", level: 95 },
      { name: "React Native", level: 88 },
      { name: "Dart", level: 92 },
      { name: "Expo", level: 85 },
      { name: "iOS/Android", level: 90 },
      { name: "State Management", level: 93 },
    ],
  },
  {
    title: "Frontend Engineering",
    skills: [
      { name: "React", level: 95 },
      { name: "Next.js", level: 92 },
      { name: "TypeScript", level: 90 },
      { name: "JavaScript", level: 95 },
      { name: "Tailwind CSS", level: 93 },
      { name: "GSAP", level: 85 },
      { name: "Shadcn UI", level: 88 },
    ],
  },
  {
    title: "Backend & Systems",
    skills: [
      { name: "Node.js", level: 90 },
      { name: "Express.js", level: 88 },
      { name: "MongoDB", level: 85 },
      { name: "PostgreSQL", level: 87 },
      { name: "Supabase", level: 82 },
      { name: "Redis", level: 80 },
      { name: "GraphQL", level: 83 },
      { name: "Socket.io", level: 86 },
    ],
  },
  {
    title: "DevOps & Tools",
    skills: [
      { name: "Docker", level: 85 },
      { name: "AWS", level: 80 },
      { name: "Drizzle ORM", level: 82 },
      { name: "Bullmq", level: 78 },
      { name: "Clerk", level: 85 },
      { name: "Bit Bucket", level: 83 },
      { name: "Prometheus", level: 75 },
      { name: "Grafana", level: 75 },
    ],
  },
  {
    title: "Design & Product",
    skills: [
      { name: "Figma", level: 90 },
      { name: "UI/UX Design", level: 88 },
      { name: "Prototyping", level: 85 },
      { name: "User Research", level: 82 },
      { name: "Product Strategy", level: 80 },
    ],
  },
];

export const EXPERIENCE_DATA: Experience[] = [
  {
    role: "Freelance Full-Stack Developer",
    company: "Family Business Conglomerate",
    period: "Jul 2026 - Present",
    description:
      "Engaged directly by a family-run business conglomerate (₹150 Cr+ revenue spanning automobile, retail, and real estate) after they found my work via organic Google search. Architected and built a multi-tenant dealership management platform from scratch for their 5-brand, 8-outlet two-wheeler dealership vertical, plus a brand website for their online identity.",
    skills: ["Next.js", "React", "Node.js", "Multi-tenant Architecture", "Full-Stack"],
    achievements: [
      "🏢 Engaged directly by a ₹150 Cr+ revenue conglomerate",
      "🏍️ Built multi-tenant platform for a 5-brand, 8-outlet dealership vertical",
      "⚡ Cut lead-to-delivery turnaround from 2 weeks to 4 days",
    ],
    type: "professional",
  },
  {
    role: "Software Developer Intern",
    company: "ESPO",
    period: "Sep 2025 - May 2026",
    description:
      "Promoted to Software Developer role. Led the development of production-ready mobile application features for Android and iOS platforms using Flutter. Collaborated with cross-functional design and product teams to implement scalable UI flows and features.",
    skills: ["Flutter", "Dart", "Android", "iOS", "Agile", "Team Leadership"],
    achievements: [
      "🚀 Promoted from Intern to Full-time Developer",
      "📱 Led mobile feature development",
      "🎨 Implemented scalable UI flows",
    ],
    type: "professional",
  },
  {
    role: "Frontend Developer Intern",
    company: "ESPO",
    period: "Jul 2025 - Sep 2025",
    description:
      "Built and refined reusable UI components for cross-platform mobile applications. Improved app responsiveness and user experience through iterative development and testing.",
    skills: ["Flutter", "UI Components", "Responsive Design"],
    achievements: [
      "✨ Built reusable UI components",
      "⚡ Improved app responsiveness",
      "🧪 Conducted iterative testing",
    ],
    type: "professional",
  },
  {
    role: "Software Development Intern",
    company: "Kushal Aide",
    period: "May 2024 - Jun 2024",
    description:
      "Developed mobile app features for a peer-to-peer handwritten assignment platform using Flutter and Firebase. Designed intuitive UI screens and built backend logic for secure data transactions.",
    skills: ["Flutter", "Firebase", "UI/UX", "Backend Logic"],
    achievements: [
      "🚀 Built features for P2P platform",
      "🔒 Implemented secure data transactions",
      "✨ Designed intuitive UI from scratch",
    ],
    type: "professional",
  },
];

export const EDUCATION_DATA: Education[] = [
  {
    school: "Maharaja Agrasen Institute of Technology",
    degree: "B.Tech in Computer Science and Technology",
    year: "Aug 2024 - Present",
    logo: "M",
    grade: "CGPA: 9.08/10",
    activities: ["Lateral Entry"],
  },
  {
    school: "Ambedkar DSEU, Shakarpur Campus",
    degree: "Diploma in Computer Science and Engineering",
    year: "Aug 2021 - Jul 2024",
    logo: "A",
    grade: "CGPA: 9.22/10",
  },
  {
    school: "Central Board of Secondary Education",
    degree: "Senior Secondary Education",
    year: "2021",
    logo: "C",
    grade: "88.8%",
  },
];

export const PHILOSOPHY_DATA: PhilosophyPrinciple[] = [
  {
    icon: "target",
    title: "Product > Code",
    description:
      "Clean code is essential, but it must serve the product. I prioritize user value and business goals over over-engineering.",
  },
  {
    icon: "zap",
    title: "UX is Everything",
    description:
      "A feature isn't complete until it feels right. I obsess over interactions, animations, and the 'feel' of the application.",
  },
  {
    icon: "rocket",
    title: "Ship & Iterate",
    description:
      "Perfection is the enemy of progress. I believe in shipping early, gathering feedback, and iterating rapidly.",
  },
  {
    icon: "book",
    title: "Always Learning",
    description:
      "Technology moves fast. I stay curious, constantly exploring new tools and patterns to build better software.",
  },
];

export const WORK_SHOWCASE_DATA: WorkShowcaseItem[] = [
  {
    id: "mobile",
    icon: "Smartphone",
    stats: {
      stack: "Flutter",
      focus: "Offline-First",
      shipped: "Loopin, Legease, Aashwasan",
    },
    title: "Mobile Apps",
    images: ["/projects/loopin_1.webp", "/projects/loopin_2.webp", "/projects/loopin_3.webp", "/projects/loopin_4.webp"],
    gradient: "from-blue-500/10",
    glowColor: "shadow-blue-500/20",
    iconColor: "text-blue-500",
    description:
      "Flutter apps with custom components, smooth animations, and polished UX.",
  },
  {
    id: "web",
    icon: "Globe",
    stats: {
      stack: "Next.js 14",
      focus: "Core Web Vitals",
      architecture: "Edge SSR",
    },
    title: "Web Apps",
    images: ["/img/code5.jpg", "/img/code6.jpg", "/img/code7.jpg", "/img/mum1.jpg"],
    gradient: "from-red-500/10",
    glowColor: "shadow-red-500/20",
    iconColor: "text-red-500",
    description: "Full-stack applications with scalable backends and modern UI/UX.",
  },
];

export const PROJECTS_DATA: Project[] = [
  {
    id: "loopin",
    title: "Loopin - RPG Habit Tracker",
    coverImage: "/projects/loopin_cover.webp",
    tagline: "Gamified Productivity with Privacy",
    description:
      "Loopin is a privacy-first habit tracker that merges RPG mechanics with offline data ownership. Built with Flutter, it features a unique centered timeline, event-driven loot drops, and atomic Google Drive sync. Every habit completion earns XP and rewards, turning discipline into character growth while ensuring data remains 100% user-owned.",
    tags: [
      "Flutter",
      "Dart",
      "Hive NoSQL",
      "Google Drive API",
      "OAuth 2.0",
      "Custom Canvas",
      "MVVM",
    ],
    year: "2026",
    featured: true,
    image: "/projects/loopin_thumb.webp",
    logo: "/projects/loopin_logo.webp",
    images: [
      "/projects/loopin_1.webp",
      "/projects/loopin_2.webp",
      "/projects/loopin_3.webp",
      "/projects/loopin_4.webp",
      "/projects/loopin_5.webp",
      "/projects/loopin_6.webp",
    ],
    isVisibleInAppOS: true,
    isVisibleInProjectsPage: true,
    isVisibleInHomeProjects: true,
    category: "Productivity",
    platform: ["iOS", "Android"],
    color: "from-green-600 to-teal-400",
  },
  {
    id: "legease",
    title: "Legease - AI Legal Document Platform",
    coverImage: "/projects/legease_cover.webp",
    tagline: "AI Legal Document Analysis",
    description:
      "Navigating legal jargon shouldn't require a law degree. Legease is an AI-driven ecosystem designed to demystify complex contracts and agreements. By leveraging advanced NLP, it provides clause-by-clause breakdowns, identifies potential risks, and connects users with legal professionals. Built with a focus on security and cross-platform accessibility, it's your personal legal assistant in your pocket.",
    tags: [
      "Flutter",
      "Dart",
      "AI/ML",
      "PDF Processing",
      "End-to-End Encryption",
      "Material Design",
    ],
    year: "2025",
    link: "https://youtube.com/shorts/rO3SDq69Fbc",
    featured: true,
    image: "/projects/legease_thumb.webp",
    logo: "/projects/legease_logo.webp",
    images: [
      "/projects/legease_1.webp",
      "/projects/legease_2.webp",
      "/projects/legease_3.webp",
      "/projects/legease_4.webp",
      "/projects/legease_5.webp",
      "/projects/legease_6.webp",
      "/projects/legease_7.webp",
      "/projects/legease_8.webp",
      "/projects/legease_9.webp",
      "/projects/legease_10.webp",
      "/projects/legease_11.webp",
      "/projects/legease_12.webp",
    ],
    video: "https://youtube.com/shorts/rO3SDq69Fbc",
    isVisibleInAppOS: true,
    isVisibleInProjectsPage: true,
    isVisibleInHomeProjects: true,
    category: "Legal Tech",
    platform: ["iOS", "Android", "Web", "Desktop"],
    color: "from-blue-600 to-blue-400",
  },
  {
    id: "aashwasan",
    title: "Aashwasan - Gig & Contract Management",
    coverImage: "/projects/aashwasan_cover.webp",
    tagline: "Empowering the Gig Economy",
    description:
      "Aashwasan is a comprehensive ecosystem designed to streamline the relationship between service providers and clients. Built with Flutter and powered by Firebase, it offers a robust platform for contract creation, real-time order tracking, and secure communication. With integrated PDF handling and push notifications, it ensures that every gig is managed with professional precision across all devices.",
    tags: ["Flutter", "Firebase", "Provider", "Dio", "Lottie", "Push Notifications"],
    year: "2025",
    featured: true,
    image: "/projects/aashwasan_thumb.webp",
    logo: "/projects/aashwasan_logo.webp",
    images: [
      "/projects/aashwasan_1.webp",
      "/projects/aashwasan_2.webp",
      "/projects/aashwasan_3.webp",
      "/projects/aashwasan_4.webp",
      "/projects/aashwasan_5.webp",
      "/projects/aashwasan_6.webp",
      "/projects/aashwasan_7.webp",
      "/projects/aashwasan_8.webp",
      "/projects/aashwasan_9.webp",
      "/projects/aashwasan_10.webp",
      "/projects/aashwasan_11.webp",
      "/projects/aashwasan_12.webp",
    ],
    isVisibleInAppOS: true,
    isVisibleInProjectsPage: true,
    isVisibleInHomeProjects: true,
    category: "Productivity",
    platform: ["iOS", "Android", "Web", "Desktop"],
    color: "from-teal-600 to-emerald-400",
  },
  {
    id: "wallly",
    title: "Wallly - 4K Wallpapers",
    coverImage: "/projects/wallly_cover.webp",
    tagline: "Ultra 4K Wallpaper Experience",
    description:
      "Wallly is more than just a wallpaper app; it's a visual journey. Designed for those who appreciate high-fidelity aesthetics, Wallly delivers a massive library of ultra 4K wallpapers through a sleek, gesture-driven interface. From nature's serenity to abstract art, every image is optimized for performance and beauty. With features like infinite scroll, smart caching, and one-click application, it's the ultimate tool for personalizing your digital space.",
    tags: [
      "Flutter",
      "Dart",
      "Material Design",
      "Cached Network Image",
      "Wallpaper Manager",
    ],
    year: "2024",
    featured: true,
    image: "/projects/wallly_thumb.webp",
    logo: "/projects/wallly_logo.webp",
    images: [
      "/projects/wallly_1.webp",
      "/projects/wallly_2.webp",
      "/projects/wallly_3.webp",
    ],
    isVisibleInAppOS: true,
    isVisibleInProjectsPage: true,
    isVisibleInHomeProjects: false,
    category: "Personalization",
    platform: ["iOS", "Android", "Web", "Desktop"],
    color: "from-purple-600 to-pink-400",
  },
  {
    id: "pokedex",
    title: "Pokedex - Ultimate Pokemon Guide",
    tagline: "Gotta Catch 'Em All, Digitally.",
    description:
      "Relive the nostalgia with a modern twist. This Pokedex app isn't just a directory; it's an interactive encyclopedia for trainers. Built with Flutter, it features real-time data fetching, high-quality SVG rendering, and authentic Pokemon cries. With smooth transitions, a sleek dark mode, and a gesture-based UI, it's designed to provide a premium experience for every Pokemon enthusiast.",
    tags: [
      "Flutter",
      "Dart",
      "Provider",
      "REST API",
      "SVG Rendering",
      "Audio Playback",
    ],
    year: "2024",
    featured: true,
    image: "/projects/pokedex_thumb.webp",
    logo: "/projects/pokedex_logo.webp",
    images: [
      "/projects/pokedex_1.webp",
      "/projects/pokedex_2.webp",
      "/projects/pokedex_3.webp",
      "/projects/pokedex_4.webp",
      "/projects/pokedex_5.webp",
      "/projects/pokedex_6.webp",
      "/projects/pokedex_7.webp",
    ],
    video: "https://youtube.com/shorts/D2QAbCzJEQE",
    isVisibleInAppOS: true,
    isVisibleInProjectsPage: true,
    isVisibleInHomeProjects: true,
    category: "Entertainment",
    platform: ["iOS", "Android", "Web"],
    color: "from-red-600 to-red-400",
  },
  {
    id: "portfolio-website",
    title: "Personal Portfolio - Engineering Excellence",
    tagline: "Vibrant, Motion-Driven Portfolio",
    description: "A high-performance personal portfolio built with Next.js, Framer Motion, and Tailwind CSS. Featuring a custom App OS simulation, immersive project showcases, and a premium magazine-style design aesthetic. Focused on micro-interactions, accessibility, and SEO optimization.",
    tags: ["Next.js", "TypeScript", "Framer Motion", "Tailwind CSS", "Three.js", "Lucide React"],
    year: "2025",
    type: "website",
    featured: true,
    image: "/projects/portfolio-website_thumb.webp",
    logo: "/projects/portfolio-website_logo.webp",
    images: [
      "/projects/portfolio-website_1.webp",
      "/projects/portfolio-website_2.webp",
      "/projects/portfolio-website_3.webp",
      "/projects/portfolio-website_4.webp",
      "/projects/portfolio-website_5.webp",
    ],
    isVisibleInAppOS: false,
    isVisibleInProjectsPage: true,
    isVisibleInHomeProjects: true,
    category: "Personal Branch",
    platform: ["Web", "Mobile"],
    color: "from-indigo-600 to-violet-400",
  },
];

export const HACKATHONS_DATA: Hackathon[] = [
  {
    date: "February 2025",
    name: "Code Kshetra 2.0",
    color: "text-green-500",
    status: "Finalist",
  },
  {
    date: "January 2025",
    name: "Level Supermind Hackathon",
    color: "text-green-500",
    status: "Finalist",
  },
  {
    date: "September 2024",
    name: "DTU Brainwave Hackathon",
    color: "text-green-500",
    status: "Finalist",
  },
];

export const CONTACT_DATA: ContactData = {
  sectionNumber: "06",
  sectionLabel: "Contact",
  headline: "Let's build something extraordinary.",
  description:
    "Whether you have a project in mind, a question about software development, or just want to say hi, I'm always open to chatting.",
  email: "sachinsharmaaofficial@gmail.com",
  cta: "Say Hello",
  location: "Noida, IN",
  onlineStatus: "Online Now",
  whatsappNumber: "918826914313",
  spotifyWidgetEnabled: true,
  socials: [
    {
      name: "github",
      href: "https://github.com/maisachinsharmahu",
      icon: "Github",
      color: "hover:text-blue-400",
    },
    {
      name: "linkedin",
      href: "https://www.linkedin.com/in/sachinsharmaofficial",
      icon: "Linkedin",
      color: "hover:text-blue-500",
    },
    {
      name: "twitter",
      href: "https://x.com/sachinsharmadev",
      icon: "Twitter",
      color: "hover:text-blue-300",
    },
  ],
};

export const MOBILE_APPS_DATA: MobileApp[] = [];
export const HIRE_ME_ITEMS: HireMeItem[] = [];

// Helper functions for static data access
/**
 * Gets projects for the home page.
 * Shows projects that are featured AND not explicitly hidden from the home page.
 */
export function getFeaturedProjects(limit?: number) {
  const projects = PROJECTS_DATA.filter(
    (p) => p.featured && p.isVisibleInHomeProjects !== false
  );
  return limit ? projects.slice(0, limit) : projects;
}

/**
 * Gets projects for the App OS simulation.
 * Shows only projects explicitly marked for the OS view.
 */
export function getProjectsAsApps() {
  return PROJECTS_DATA.filter((p) => p.isVisibleInAppOS === true);
}

export function projectToApp(project: Project) {
  // Fallback to static data if remote Supabase JSON is missing the logo attribute
  const staticMatch = PROJECTS_DATA.find((p) => p.id === project.id);
  
  return {
    id: project.id || Math.random().toString(36).substr(2, 9),
    // Home-screen icons need just the app name (e.g. "Loopin"), not the full
    // marketing title ("Loopin - RPG Habit Tracker").
    name: project.title.split(" - ")[0],
    tagline: project.tagline || "",
    description: project.description,
    category: project.category || "General",
    downloads: project.downloads || "0",
    rating: project.rating || 0,
    platform: project.platform || [],
    year: project.year,
    color: project.color || "from-blue-600 to-blue-400",
    tech: project.tags,
    screens: project.images || [],
    logo: project.logo || staticMatch?.logo,
  };
}
export const ANIMATED_WORDS = {
  left: [
    "Scalable Webapps",
    "Frontend Magic",
    "User Interfaces",
    "Simple Scripts",
  ],
  right: [
    "Enterprise Solutions",
    "Cloud Architecture",
    "Backend Systems",
    "Complex Mobile Apps",
  ],
};
